import { useT } from '@transifex/react'
import { useValidatePostCode } from '@vend/business-components/dist/utils/validators/postcodeValidator'
import {
  CANADA_COUNTRY_CODE,
  US_COUNTRY_CODE,
  VD_COUNTRY_CODES,
} from '../../../../../../vend-ui/constants'
import { findStateOrProvince } from '../../popover/utils/addressHelper'

interface CountryCodesMap {
  [countryCode: string]: { code: string; name: string }
}

export interface AddressFieldValidationResult {
  isValid: boolean
  errorMessage: string
}

const countryCodesMap: CountryCodesMap = VD_COUNTRY_CODES.reduce(
  (map, country) => {
    map[country.code] = country
    return map
  },
  {} as CountryCodesMap
)

const MAX_ADDRESS_LENGTH = 50
const MAX_CITY_LENGTH = 28
const MAX_STATE_LENGTH = 35
const COUNTRIES_WITH_STATES = [US_COUNTRY_CODE, CANADA_COUNTRY_CODE]
const DEFAULT_ERROR_RESULT: AddressFieldValidationResult = {
  isValid: false,
  errorMessage: '',
}
const DEFAULT_SUCCESS_RESULT: AddressFieldValidationResult = {
  isValid: true,
  errorMessage: '',
}

const isStateRequired = (country_id: string): boolean => {
  return COUNTRIES_WITH_STATES.includes(country_id.toUpperCase())
}

const isStateValid = (state: string, country_id: string): boolean => {
  state = state.toUpperCase()

  return findStateOrProvince(state, country_id) !== undefined
}

export const useAddressValidation = () => {
  const t = useT()
  const validatePostCode = useValidatePostCode()

  const streetAddress1Validation = (
    address_1: string = ''
  ): AddressFieldValidationResult => {
    if (!address_1)
      return {
        ...DEFAULT_ERROR_RESULT,
        errorMessage: t('Address is required', { _context: 'Error text' }),
      }

    if (address_1.length > MAX_ADDRESS_LENGTH)
      return {
        ...DEFAULT_ERROR_RESULT,
        errorMessage: t(
          'Address must be no longer than {maxLength} characters',
          { maxLength: MAX_ADDRESS_LENGTH, _context: 'Error text' }
        ),
      }

    return DEFAULT_SUCCESS_RESULT
  }

  const streetAddress2Validation = (
    address_2: string = ''
  ): AddressFieldValidationResult => {
    if (address_2.length > MAX_ADDRESS_LENGTH)
      return {
        ...DEFAULT_ERROR_RESULT,
        errorMessage: t(
          'Address must be no longer than {maxLength} characters',
          { maxLength: MAX_ADDRESS_LENGTH, _context: 'Error text' }
        ),
      }

    return DEFAULT_SUCCESS_RESULT
  }

  const cityValidation = (city: string = ''): AddressFieldValidationResult => {
    if (!city)
      return {
        ...DEFAULT_ERROR_RESULT,
        errorMessage: t('City is required', { _context: 'Error text' }),
      }

    if (city.length > MAX_CITY_LENGTH)
      return {
        ...DEFAULT_ERROR_RESULT,
        errorMessage: t(
          'City name must be no longer than {maxLength} characters',
          { maxLength: MAX_CITY_LENGTH, _context: 'Error text' }
        ),
      }

    return DEFAULT_SUCCESS_RESULT
  }

  const stateValidation = (
    state: string = '',
    country_id: string = ''
  ): AddressFieldValidationResult => {
    // Require & validate states only for US/CA Addresses
    if (isStateRequired(country_id)) {
      if (!state)
        return {
          ...DEFAULT_ERROR_RESULT,
          errorMessage: t('State is required', { _context: 'Error text' }),
        }

      if (!isStateValid(state, country_id))
        return {
          ...DEFAULT_ERROR_RESULT,
          errorMessage: t(
            'The state name is incorrect. Please select a state from the list',
            { _context: 'Error text' }
          ),
        }

      return DEFAULT_SUCCESS_RESULT
    }

    // Check only the max-length for all other countries
    if (state.length > MAX_STATE_LENGTH)
      return {
        ...DEFAULT_ERROR_RESULT,
        errorMessage: t(
          'State name must be no longer than {maxLength} characters',
          { maxLength: MAX_STATE_LENGTH, _context: 'Error text' }
        ),
      }

    return DEFAULT_SUCCESS_RESULT
  }

  const countryValidation = (
    country_id: string = ''
  ): AddressFieldValidationResult => {
    if (!country_id)
      return {
        ...DEFAULT_ERROR_RESULT,
        errorMessage: t('Country is required', { _context: 'Error text' }),
      }

    if (countryCodesMap[country_id] === undefined)
      return {
        ...DEFAULT_ERROR_RESULT,
        errorMessage: t('Invalid country', { _context: 'Error text' }),
      }

    return DEFAULT_SUCCESS_RESULT
  }

  const zipcodeValidation = (
    zipcode: string = '',
    country_id: string = ''
  ): AddressFieldValidationResult => {
    if (!zipcode) {
      return {
        ...DEFAULT_ERROR_RESULT,
        errorMessage:
          country_id.toUpperCase() !== US_COUNTRY_CODE
            ? t('Postal code is required', { _context: 'Error text' })
            : t('ZIP code is required', { _context: 'Error text' }),
      }
    }
    const { valid: isValid, errorMessage } = validatePostCode(
      zipcode,
      country_id
    )
    return { isValid, errorMessage: errorMessage || '' }
  }

  return {
    streetAddress1Validation,
    streetAddress2Validation,
    cityValidation,
    stateValidation,
    countryValidation,
    zipcodeValidation,
  }
}
